import { context } from 'cadenza/map/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { inView } from 'ol/layer/Layer';
import ViewProperty from 'ol/ViewProperty';
import VectorImageLayer from 'ol/layer/VectorImage';
import { PhotoLayerApi } from 'cadenza/api-client/photolayer-api';

import { FeatureInfo, TEXT_HTML_INFO_FORMAT } from '../interactions/feature-info';

import { PhotoLayerSource } from './photo-layer-source';
import { photoLayerStyleFunction as photoLayerStyle } from './photo-layer-style';
import { PhotoLayerPointerInteraction, PhotoLayerEventType } from './photo-layer-pointer-interaction';
import { featureSortFunction, photoFeatureLocation } from './photo-layer-utils';

export class PhotoLayer extends VectorImageLayer {

  constructor ({ masterLayerId, map }) {
    super({
      renderOrder: featureSortFunction,
      source: new PhotoLayerSource({ masterLayerId, map }),
      style: photoLayerStyle,
      updateWhileInteracting: true
    });

    this._map = map;
    this._masterLayer = context(map).getLayer(masterLayerId);
    this._dragInteraction = new PhotoLayerPointerInteraction(this.getSource());
    this._dragInteraction.on(PhotoLayerEventType.PHOTO_CLICK, e => this._onPhotoClick(e));
    map.addInteraction(this._dragInteraction);
    this._updateDragInteraction();

    map.getLayers().on('remove', event => {
      if (event.element === this._masterLayer) {
        map.removeInteraction(this._dragInteraction);
        map.removeLayer(this);
      }
    });

    this._masterLayer.on(`change:${LayerProperty.VISIBLE}`, () => {
      this.set(LayerProperty.VISIBLE, this._masterLayer.getVisible());
      this._updateDragInteraction();
    });
    map.getView().on(`change:${ViewProperty.RESOLUTION}`, () => this._updateDragInteraction());
  }

  _onPhotoClick (event) {
    if (this._masterLayer !== this._map.get(MapProperty.SELECTED_LAYER)) {
      return;
    }
    const featureInfoInteraction = getFeatureInfoInteraction(this._map);
    if (!featureInfoInteraction) {
      return;
    }
    event.preventDefault();
    const coordinate = photoFeatureLocation(event.feature);
    const pixel = this._map.getPixelFromCoordinate(coordinate).map(Math.round);
    const originalMouseEvent = event.originalEvent.originalEvent;
    const pageCoordinates = [ originalMouseEvent.clientX, originalMouseEvent.clientY ];
    featureInfoInteraction.makeRequest(pixel, coordinate, pageCoordinates, TEXT_HTML_INFO_FORMAT);
  }

  _updateDragInteraction () {
    this._dragInteraction.setActive(inView(this.getLayerState(), this._map.getView().getState()));
  }

  preparePrint () {
    // To test photo displacements, select a template with a fixed scale ("Accurate to scale"/"maßstabsgetreu").
    const srsCode = this._map.getView().getProjection().getCode();
    const mapScale = context(this._map).mapScale;
    const visibleArea = this._map.getView().calculateVisibleExtent();
    const displacements = this.getSource().serializeDisplacements();
    new PhotoLayerApi(this._map.get(MapProperty.ID),
      this.getSource().get('masterlayer'))
      .preparePrint(srsCode, mapScale, visibleArea, displacements);
  }

}

function getFeatureInfoInteraction (map) {
  return map.getInteractions().getArray().find((interaction) => interaction instanceof FeatureInfo);
}
